// Copied into your app by RapidRailsUI (bin/rails rui:update).
// Don't edit this copy: the next update overwrites it. Fix it in the rapid_rails_ui gem.
//
// RapidRailsUI JavaScript diagnostics. No imports on purpose: this file must load
// even when every other gem module is missing or broken, so that a failure is
// reported with the component and file named instead of dying silently.
//
// What it does:
// - ruiLog(component, message, { element, cause }) prints one tagged line:
//     [RapidRailsUI] chart#update-heat: could not load shared/chart_geometry (cause)
// - ruiMark(element, message) sets data-rui-error and, in development, shows a
//   small notice inside the component (role="alert").
// - watchConnections(application, ids) runs after the page settles (and after each
//   turbo:load) and reports every element with data-controller="<id>" that Stimulus
//   never gave a controller: the symptom of a controller file missing from the app,
//   an import that failed, or a registration that threw.
// - loadGuard(importer, label) imports rapid_rails_ui/stimulus_guard dynamically and
//   reports a named failure when that import rejects (a static import would take the
//   whole entry file down with a bare "Failed to resolve module specifier").
//
// Development is detected from <html data-rui-env="development"> (the layout
// helper writes it) or <meta name="rui-env" content="development">.

const TAG = "[RapidRailsUI]"

export function isDevelopment(doc = document) {
  const html = doc.documentElement?.dataset?.ruiEnv
  if (html) return html === "development"
  const meta = doc.querySelector?.('meta[name="rui-env"]')
  return meta?.content === "development"
}

export function describe(element) {
  if (!element) return ""
  if (element.id) return `#${element.id}`
  const cls = (element.className && typeof element.className === "string") ? element.className.split(/\s+/).filter(Boolean).slice(0, 2).join(".") : ""
  return `${element.tagName?.toLowerCase() || "element"}${cls ? "." + cls : ""}`
}

export function formatMessage(component, message, { element = null, cause = null } = {}) {
  const where = describe(element)
  const head = where ? `${component}${where.startsWith("#") ? where : " " + where}` : component
  const why = cause ? ` (${cause instanceof Error ? cause.message : String(cause)})` : ""
  return `${TAG} ${head}: ${message}${why}`
}

export function ruiLog(component, message, options = {}, { logger = console } = {}) {
  const text = formatMessage(component, message, options)
  logger.error(text)
  if (options.element) ruiMark(options.element, `${component}: ${message}`)
  return text
}

export function ruiMark(element, message, { dev = isDevelopment() } = {}) {
  if (!element) return
  element.setAttribute("data-rui-error", message)
  if (!dev || element.querySelector(":scope > [data-rui-notice]")) return
  const notice = document.createElement("div")
  notice.setAttribute("data-rui-notice", "")
  notice.setAttribute("role", "alert")
  notice.style.cssText = "margin:4px 0;padding:4px 8px;font:12px/1.4 ui-monospace,monospace;border:1px dashed currentColor;border-radius:4px;opacity:.85"
  notice.textContent = `${TAG} ${message}. Run bin/rails rui:doctor.`
  element.prepend(notice)
}

// After `delay` ms (and again after each turbo:load), report every gem component on
// the page that Stimulus never connected. `ids` are the full identifiers registerAll
// returned ("rui--chart"); the file name in the message is derived from the bare id.
export function watchConnections(application, ids, { delay = 1500, doc = document, logger = console, turbo = true } = {}) {
  const run = () => {
    const missing = []
    for (const id of ids) {
      doc.querySelectorAll(`[data-controller~="${id}"]`).forEach((el) => {
        if (!application.getControllerForElementAndIdentifier(el, id) && !el.hasAttribute?.("data-rui-error")) {
          missing.push([id, el])
        }
      })
    }
    for (const [id, el] of missing) {
      const file = id.split("--").pop().replace(/-/g, "_")
      ruiLog(id, `controller never connected; check app/javascript/controllers/rapid_rails_ui/${file}_controller.js exists and is registered`, { element: el }, { logger })
    }
    return missing
  }
  if (turbo && typeof doc.addEventListener === "function") {
    doc.addEventListener("turbo:load", () => setTimeout(run, delay))
  }
  if (typeof setTimeout === "function") return new Promise((resolve) => setTimeout(() => resolve(run()), delay))
  return Promise.resolve(run())
}

// Import the guard without letting a broken module take the page down silently.
// The caller passes the import as a function with a literal specifier, so bundlers
// (esbuild) can still see and bundle it:
//   loadGuard(() => import("rapid_rails_ui/stimulus_guard"), "rapid_rails_ui/stimulus_guard")
export async function loadGuard(importer, label = "rapid_rails_ui/stimulus_guard", { logger = console } = {}) {
  try {
    return await importer()
  } catch (cause) {
    ruiLog("stimulus_guard", `could not load ${label}; no RapidRailsUI controller will work. Run bin/rails rui:doctor`, { cause }, { logger })
    return null
  }
}
